// Cosmetic reactions layered over the real conversation state. A stream of
// token updates must not keep restarting the character's reaction timer.
const WORKING = new Set(['thinking', 'tool', 'agentTyping'])
const REACTIONS = ['blink', 'nod', 'pop']
const REACTION_INTERVAL_MS = 2800

/**
 * Create an activity controller that changes the bot's pose and schedules
 * repeating reactions while typing or working. Repeating the same activity
 * leaves its timer running; disabling motion keeps only the activity pose.
 * @param {object} bot - The character exposing setState and trigger methods.
 * @param {object} [options] - Timer implementations and the initial motion preference.
 * @returns {object} Methods to update activity, toggle motion and cancel work on destruction.
 */
export function createBoxbotActivity (bot, {
  schedule = setTimeout,
  cancel = clearTimeout,
  motion = true,
  calmIdle = false
} = {}) {
  let state
  let phase = 0
  let timer
  let destroyed = false

  function clear () {
    if (timer !== undefined) { cancel(timer) }
    timer = undefined
  }

  function paint () {
    bot.setReducedMotion?.(!motion || (calmIdle && state === 'idle'))
    // Alternate attentive expressions while the user types. Working poses
    // stay tied to the server's activity, with fresh variants and gestures.
    bot.setState(motion && state === 'userTyping' && phase % 2 ? 'hover' : state)
    if (motion && phase > 0) { bot.trigger(REACTIONS[(phase - 1) % REACTIONS.length]) }
    if (motion && (state === 'userTyping' || WORKING.has(state))) {
      timer = schedule(() => {
        timer = undefined
        phase += 1
        paint()
      }, REACTION_INTERVAL_MS)
    }
  }

  return {
    update (next) {
      if (destroyed || next === state) { return }
      clear()
      state = next
      phase = 0
      paint()
      if (motion && state === 'userTyping') { bot.trigger('nod') }
    },
    setMotion (enabled) {
      if (destroyed || enabled === motion) { return }
      clear()
      motion = enabled
      phase = 0
      if (state) { paint() }
    },
    destroy () {
      destroyed = true
      clear()
    }
  }
}
